import React, { useMemo, forwardRef } from 'react';
import { SvgIconProps } from '@mui/material';
import { IconNameProp, iconDefinitions } from './icon.definitions';

/**
 * @interface IconProps
 * @description Base definition of SkodaFlow Icon
 */
export interface IconProps extends SvgIconProps {
  /**
   * Name of the icon that should be used. Alternatively, provide name in the className property
   */
  name?: IconNameProp;
  /**
   * Style of vectors, regular (stroke) or filled format
   * @default 'regular'
   */
  iconStyle?: 'regular' | 'filled';
}

/**
 * SkodaFlow Icon component
 * ! usage of component is not recommended as it increases bundle size
 */
export const Icon: React.FC<IconProps> = forwardRef(({ name, className, iconStyle, ...rest }, ref) => {
  // obtain SvgIcon component to be rendered
  const IconComponent = useMemo(() => {
    const definition =
      // try to find by name
      (name && iconDefinitions.find((def) => [def.name, ...def.altNames].includes(name))) ||
      // try to find by className
      (className &&
        iconDefinitions.find((def) => className.split(' ').some((cls) => [def.name, ...def.altNames].includes(cls)))) ||
      // fallback
      iconDefinitions.find((item) => item.name === 'circle-question');
    return iconStyle === 'filled' ? definition.componentFilled : definition.component;
  }, [name, className, iconStyle]);

  return (
    <IconComponent
      ref={ref}
      data-testid="Icon"
      className={['sa-i', className, name].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});
